import { zodResolver } from '@hookform/resolvers/zod';
import { QRCodeSVG } from 'qrcode.react';
import { find, propEq, values } from 'ramda';
import React, { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import CodeInput from '@ui/atoms/CodeInput/CodeInput.component';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import FolxLoader from '@ui/atoms/FolxLoader';
import HeadingText from '@ui/atoms/HeadingText';

import aligned from '@ui/styled/Aligned';

import { AuthStep, AuthStepContext, useAuth } from '@providers/AuthProvider';

import awsAuthErrorMessages, { AwsAuthErrorMessageValue } from '../awsAuthErrorMessages';

interface ITOTPCodeInputFormProps {
	authStep: AuthStepContext;
	setAuthStep: (authContext: AuthStepContext) => void;
	pageContent: ITOTPCodeInputFormContent;
	onComplete: () => void;
}

export interface ITOTPCodeInputFormContent {
	title: string;
	changeNumberLink: string;
	description: string;
	codeSentMessage: string;
	codeWaitingMessage: string;
	resendLink: string;
}

const AlignedColumn = aligned<IFlexColumn>(FlexColumn);

const CodeText = styled(BasicText)`
	width: 24rem;
	text-align: center;
	overflow-wrap: break-word;
`;

const TOTPCodeInputForm: FC<ITOTPCodeInputFormProps> = ({
	authStep,
	setAuthStep,
	pageContent,
	onComplete,
}) => {
	const { verifyTOTPMultifactor, verifyTOTPMultifactorSetup } = useAuth();

	const [submitting, setSubmitting] = useState(false);

	const schema = z.object({
		verificationCode: z.string().min(6, { message: 'A valid verification code is required.' }),
	});

	type Schema = Partial<z.input<typeof schema>>;

	const {
		handleSubmit,
		setError,
		control,
		formState: { errors },
	} = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onSubmit',
		reValidateMode: 'onSubmit',
	});

	const { title, description } = pageContent;

	const onSubmit = async (data: Schema) => {
		try {
			setSubmitting(true);
			let newAuthStep = authStep;
			if (authStep.nextStep === AuthStep.needTOTPMultifactorVerification) {
				newAuthStep = await verifyTOTPMultifactor(authStep.user, data.verificationCode as string);
			} else if (authStep.nextStep === AuthStep.needTOTPMultifactorSetup) {
				newAuthStep = await verifyTOTPMultifactorSetup(
					authStep.user,
					data.verificationCode as string
				);
			}

			if (newAuthStep.nextStep === AuthStep.done) {
				onComplete();
			}

			setAuthStep(newAuthStep);
		} catch (e: any) {
			const errorMessage: AwsAuthErrorMessageValue | undefined = find<AwsAuthErrorMessageValue>(
				propEq('awsErrorMessage', e?.message ?? ''),
				values(awsAuthErrorMessages)
			);

			if (e?.message && e.message.includes('User is not authenticated')) {
				setAuthStep({ nextStep: AuthStep.needPassword });
			}

			switch (e?.code) {
				case 'NotAuthorizedException':
					if (
						e?.message === awsAuthErrorMessages.expiredSession.awsErrorMessage ||
						e?.message === awsAuthErrorMessages.invalidSession.awsErrorMessage
					) {
						setAuthStep({ nextStep: AuthStep.needPassword });
						break;
					} else {
						setError('verificationCode', {
							type: 'manual',
							message:
								errorMessage?.userErrorMessage ??
								awsAuthErrorMessages.invalidSmsCode.userErrorMessage,
						});
					}
					break;
				case 'ExpiredCodeException':
					setError('verificationCode', {
						type: 'manual',
						message: awsAuthErrorMessages.expiredSmsCode.userErrorMessage,
					});
					break;
				default:
					setError('verificationCode', {
						type: 'manual',
						message:
							errorMessage?.userErrorMessage ??
							awsAuthErrorMessages.invalidSmsCode.userErrorMessage,
					});
			}
		} finally {
			setSubmitting(false);
		}
	};

	if (submitting) {
		return <FolxLoader />;
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FlexColumn gap="xl">
				<FlexColumn gap="sm">
					<HeadingText>{title}</HeadingText>
					<BasicText>{description}</BasicText>
				</FlexColumn>
				{authStep.nextStep === AuthStep.needTOTPMultifactorSetup && 'secretCode' in authStep && (
					<AlignedColumn gap="sm" justifyContent="center" alignItems="center">
						<QRCodeSVG value={`otpauth://totp/Folx%20Health?secret=${authStep.secretCode}`} />
						<CodeText variant="fineprint">{authStep.secretCode}</CodeText>
					</AlignedColumn>
				)}
				<Controller
					name="verificationCode"
					control={control}
					defaultValue=""
					render={({ field }) => (
						<CodeInput
							maxLength={6}
							{...field}
							error={errors?.verificationCode?.message}
							onComplete={handleSubmit(onSubmit)}
						/>
					)}
				/>
			</FlexColumn>
		</form>
	);
};

export default TOTPCodeInputForm;
